// core/torrent-add.js — R1b.next: add a .torrent/magnet to zapd's torrent engine.
//
// Talks to /api-zapd (NOT the PHP /api/* that core/api.js targets). zapd's
// authenticate() reads a Bearer header (or the cookie), so set it explicitly from
// auth.getToken(). The multipart upload uses a bare fetch WITHOUT a Content-Type
// so the browser generates the multipart boundary itself — routing FormData
// through a JSON-setting wrapper would corrupt it.
import { auth } from './auth.js';

const ZAPD_BASE = '/api-zapd';

function bearer() {
  const t = auth.getToken();
  return t ? { Authorization: 'Bearer ' + t } : {};
}

// Unwrap the V0 envelope { ok, data, toast }; throw the server message on failure.
async function unwrap(r) {
  let body = {};
  try {
    body = await r.json();
  } catch {
    /* non-JSON body (e.g. a proxy error page) */
  }
  if (!r.ok || body.ok === false) {
    throw new Error(body.error || body.toast || 'HTTP ' + r.status);
  }
  return body.data ?? body;
}

// Upload a .torrent File/Blob. Resolves to the envelope data:
//   { torrent_id, info_hash, name, total_size, has_metadata:true, was_new,
//     media:{ file_index, size, name, torrent_uri } }
// or throws "no_media_file" when the torrent has no playable video.
// opts.import=true marks a library add (S3 Serien): the server sets
// torrent.source='import' and the extract pipeline sorts the payload into
// the series tree when the download finishes.
export async function addTorrentBlob(file, opts = {}) {
  const fd = new FormData();
  fd.append('file', file, file.name || 'upload.torrent');
  const r = await fetch(ZAPD_BASE + '/torrent/open' + (opts.import ? '?import=1' : ''), {
    method: 'POST',
    credentials: 'include',
    headers: { ...bearer() }, // NO Content-Type — browser sets the boundary
    body: fd,
  });
  return unwrap(r);
}

// Add a magnet URI. Resolves to { torrent_id, has_metadata:false, needs_metadata:true }.
// opts.import as in addTorrentBlob.
export async function addMagnet(uri, opts = {}) {
  const r = await fetch(ZAPD_BASE + '/torrent/open' + (opts.import ? '?import=1' : ''), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...bearer() },
    body: JSON.stringify({ magnet: uri }),
  });
  return unwrap(r);
}

// S3 Serien "Jetzt einsortieren": force re-process (extract/organize) for a
// finished/existing torrent. Resolves to { torrent_id, job_id }.
export async function processTorrentNow(id) {
  const r = await fetch(ZAPD_BASE + '/torrent/' + id + '/process', {
    method: 'POST',
    credentials: 'include',
    headers: { ...bearer() },
  });
  return unwrap(r);
}

// Poll a torrent's full row (has_metadata, media_file_index, info_hash, runtime).
// Shape-tolerant: the row may sit under data.torrent or be the data itself.
export async function torrentStatus(id) {
  const r = await fetch(ZAPD_BASE + '/v1/torrent/' + encodeURIComponent(id), {
    method: 'GET',
    credentials: 'include',
    headers: { ...bearer() },
  });
  const data = await unwrap(r);
  return data.torrent ?? data;
}
